import { ILegalEntityAuditClassificationReportProps } from "./LegalEntityAuditClassificationReport.tsx";
import { useParams } from "react-router";
import {
    LegalEntityRiskAuditClassificationReportRow,
    LegalEntityRiskAuditClassificationReportViewModel,
} from "./LegalEntityAuditClassificationReport.types.ts";
import { useStateUrlParamsArray } from "../../common/hooks/useStateUrlParams.ts";
import { DotLegalSelectOption } from "../../common/components/dotLegalMultiSelect/DotLegalMultiSelect.types.ts";
import { RiskLevel } from "../legalEntityClassificationTab/LegalEntityClassificationTab.types.ts";
import { useDownloadLegalEntityAuditReport } from "../../legalEntityAuditGroup/DowloadLegalEntityAuditReport.hooks.ts";
import { useDownloadLegalEntityClassificationReport } from "../legalEntityClassificationTab/DownloadLegalEntityClassificationReport.hooks.ts";
import { createElement, useState } from "react";
import { Trans } from "react-i18next";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { getSettings } from "../../common/settingsProvider.ts";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider.tsx";
import { createPdfUrl } from "../../common/pdfUrlHelper.ts";
import { downloadFile } from "../../common/api/downloadFile.ts";

import { useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";

export function useLegalEntityAuditClassificationReport(props: ILegalEntityAuditClassificationReportProps) {
    const { id, scope } = useParams<{ id: string; scope: string }>();
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const isPdf = Boolean(urlParams.get("isPdf"));
    const [searchedRiskLevel, setSearchedRiskLevel] = useStateUrlParamsArray("riskLevel", []);
    const [searchedScore, setSearchedScore] = useStateUrlParamsArray("auditScore", []);
    const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
    const snackbar = useDotLegalSnackbar();
    const { language } = useUserContext();
    let riskLevelFilterOptions: Array<DotLegalSelectOption> = [];
    let scoreFilterOptions: Array<DotLegalSelectOption> = [];
    const {
        onDownloadReportAsPdf: onDownloadAuditReportAsPdf,
        getReportPdfUrl: getAuditReportPdfUrl,
        isDownloadingPdf: isDownloadingAuditPdf,
    } = useDownloadLegalEntityAuditReport();

    const {
        onDownloadReportAsPdf: onDownloadClassificationReportAsPdf,
        getReportPdfUrl: getClassificationReportPdfUrl,
        isDownloadingPdf: isDownloadingClassificationPdf,
    } = useDownloadLegalEntityClassificationReport();

    function createAuditRiskLevelOptions(riskLevels: Array<{ level: RiskLevel; name: string | undefined }>) {
        riskLevels.forEach((item) => {
            if (item && !riskLevelFilterOptions.some((x) => x.id === item.level.toString())) {
                riskLevelFilterOptions.push({
                    id: item.level.toString(),
                    name: item.name ?? "",
                });
            }
        });
    }

    function createScoreOptions(scores: Array<number>) {
        scores.forEach((score) => {
            if (!scoreFilterOptions.some((x) => x.id === score.toString())) {
                scoreFilterOptions.push({
                    id: score.toString(),
                    name: `${score} %`,
                });
            }
        });
    }

    const url = `/${props.type === "classification" ? "LegalEntityRiskClassificationReport" : "LegalEntityRiskAuditReport"}/${id}/${scope}`;
    const { isLoading, data } = useDotLegalQuery<LegalEntityRiskAuditClassificationReportViewModel>(url);

    if (data?.rows) {
        createAuditRiskLevelOptions(data?.rows.map((x) => ({ level: x.riskLevel, name: x.riskName })));
        createScoreOptions(data?.rows.flatMap((x) => x.score));

        sortSelectOptions();
    }

    function sortSelectOptions() {
        riskLevelFilterOptions.sort((a, b) => (a.name > b.name ? 1 : -1));
        scoreFilterOptions.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    }

    function getFilteredRows(rows: Array<LegalEntityRiskAuditClassificationReportRow>) {
        let tempData = [...rows];

        if (searchedRiskLevel.length > 0 && searchedRiskLevel[0] !== "") {
            tempData = tempData.filter((row) => row.riskLevel && searchedRiskLevel.includes(row.riskLevel.toString()));
        }

        if (searchedScore.length > 0 && searchedScore[0] !== "") {
            tempData = tempData.filter((row) => searchedScore.includes(row.score.toString()));
        }
        return tempData;
    }

    async function downloadReport(reportName: string) {
        setIsDownloadingPdf(true);
        let url = location.pathname + "?isPdf=true";
        url = createPdfUrl(url, language);
        const fileName = `${reportName}.pdf`;
        await downloadFile(getSettings().apiBaseUrl + "/pdf/generate?noMargin=true&url=" + url, fileName);
        snackbar.show(createElement(Trans, { i18nKey: "pdfDownloadComplete", values: { fileName: fileName } }));
        setIsDownloadingPdf(false);
    }

    return {
        data,
        rows: getFilteredRows(data?.rows ?? []),
        isLoading,
        isPdf,
        searchedRiskLevel,
        setSearchedRiskLevel,
        riskLevelFilterOptions,
        searchedScore,
        setSearchedScore,
        scoreFilterOptions,
        onDownloadAuditReportAsPdf,
        getAuditReportPdfUrl,
        isDownloadingAuditPdf,
        onDownloadClassificationReportAsPdf,
        getClassificationReportPdfUrl,
        isDownloadingClassificationPdf,
        downloadReport,
        isDownloadingPdf,
    };
}
